import { onMounted, onUnmounted, ref, type Ref } from 'vue'

export interface UseDistanceFromCenter {
    /** Distance in pixels from the element's center to the viewport center (negative = above, positive = below) */
    distance: Ref<number>
    /** Normalized value between -1 and 1 (0 = centered, -1 = top of viewport, 1 = bottom) */
    normalized: Ref<number>
    /** True when the element is within the viewport */
    isVisible: Ref<boolean>
    /** The element's center Y position relative to the viewport */
    elementCenterY: Ref<number>
    /** The viewport's center Y position */
    viewportCenterY: Ref<number>
}

/**
 * Tracks how far an element is from the vertical center of the viewport.
 *
 * @param target - A template ref or a getter returning an HTMLElement
 * @param options.throttle - Milliseconds to throttle scroll/resize events (default: 0)
 *
 * @example
 * <script setup>
 * const el = ref(null)
 * const { distance, normalized } = useDistanceFromCenter(el)
 * </script>
 *
 * <template>
 *   <div ref="el">I am {{ distance }}px from center ({{ normalized }} normalized)</div>
 * </template>
 */
export function useDistanceFromCenter(
        target: Ref<HTMLElement | null | undefined>,
        options: { throttle?: number } = {}
): UseDistanceFromCenter {
    const distance = ref(0)
    const normalized = ref(0)
    const isVisible = ref(false)
    const elementCenterY = ref(0)
    const viewportCenterY = ref(0)

    let rafId: number | null = null
    let lastRun = 0
    const throttleMs = options.throttle ?? 0

    function calculate() {
        const el = target.value
        if (!el) return

        const rect = el.getBoundingClientRect()
        const vh = window.innerHeight

        const elCenter = rect.top + rect.height / 2
        const vpCenter = vh / 2

        const dist = elCenter - vpCenter

        elementCenterY.value = elCenter
        viewportCenterY.value = vpCenter
        distance.value = Math.round(dist * 100) / 100

        // Normalize: clamp to [-1, 1] relative to half the viewport height
        normalized.value = Math.max(-1, Math.min(1, dist / (vh / 2)))

        // Visible if any part of the element is in the viewport
        isVisible.value = rect.bottom > 0 && rect.top < vh
    }

    function onEvent() {
        if (throttleMs === 0) {
            if (rafId !== null) cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(() => {
                calculate()
                rafId = null
            })
            return
        }

        const now = Date.now()
        if (now - lastRun >= throttleMs) {
            lastRun = now
            if (rafId !== null) cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(() => {
                calculate()
                rafId = null
            })
        }
    }

    onMounted(() => {
        calculate()
        window.addEventListener('scroll', onEvent, { passive: true })
        window.addEventListener('resize', onEvent, { passive: true })
    })

    onUnmounted(() => {
        window.removeEventListener('scroll', onEvent)
        window.removeEventListener('resize', onEvent)
        if (rafId !== null) cancelAnimationFrame(rafId)
    })

    return { distance, normalized, isVisible, elementCenterY, viewportCenterY }
}